import React, { useEffect, useState, useRef } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTutorialContext } from '../../contexts/TutorialContext';
import './Tutorial.scss';

const Tutorial = () => {
  const {
    isActive,
    currentStepData,
    currentStep,
    totalSteps,
    progress,
    nextStep,
    prevStep,
    stopTutorial,
    startTutorial,
    shouldShowTutorial,
    isFirstStep,
    isLastStep
  } = useTutorialContext();

  const [targetElement, setTargetElement] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const tooltipRef = useRef(null);

  // Fonction pour vérifier si un élément est visible
  const isElementVisible = (element) => {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           rect.width > 0 && 
           rect.height > 0;
  };

  // Fonction pour trouver l'élément cible
  const findTargetElement = (selector) => {
    if (!selector) return null;
    
    // Sélecteurs multiples séparés par des virgules
    const selectors = selector.split(',').map(s => s.trim());
    
    for (const sel of selectors) {
      // Gestion des sélecteurs spéciaux :contains()
      if (sel.includes(':contains(')) {
        const match = sel.match(/^(.+?):contains\("([^"]+)"\)$/);
        if (match) {
          const [, baseSelector, text] = match;
          const elements = Array.from(document.querySelectorAll(baseSelector || 'button, a, div, span'));
          const element = elements.find(el => el.textContent.includes(text) && isElementVisible(el));
          if (element) return element;
        }
      } else {
        // Sélecteur CSS normal
        const element = document.querySelector(sel);
        if (element && isElementVisible(element)) return element;
      }
    }
    
    return null;
  };

  // Calculer la position du tooltip
  const calculateTooltipPosition = (target, position) => {
    if (!tooltipRef.current) return { top: 0, left: 0 };

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let top, left;

    switch (position) {
      case 'center':
        // Pour le centre, on n'a pas besoin de target
        top = window.innerHeight / 2 - tooltipRect.height / 2 + scrollY;
        left = window.innerWidth / 2 - tooltipRect.width / 2 + scrollX;
        break;
      case 'top':
        if (!target) return { top: 0, left: 0 };
        const targetRectTop = target.getBoundingClientRect();
        top = targetRectTop.top + scrollY - tooltipRect.height - 20;
        left = targetRectTop.left + scrollX + (targetRectTop.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        if (!target) return { top: 0, left: 0 };
        const targetRectBottom = target.getBoundingClientRect();
        top = targetRectBottom.bottom + scrollY + 20;
        left = targetRectBottom.left + scrollX + (targetRectBottom.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        if (!target) return { top: 0, left: 0 };
        const targetRectLeft = target.getBoundingClientRect();
        top = targetRectLeft.top + scrollY + (targetRectLeft.height / 2) - (tooltipRect.height / 2);
        left = targetRectLeft.left + scrollX - tooltipRect.width - 20;
        break;
      case 'right':
        if (!target) return { top: 0, left: 0 };
        const targetRectRight = target.getBoundingClientRect();
        top = targetRectRight.top + scrollY + (targetRectRight.height / 2) - (tooltipRect.height / 2);
        left = targetRectRight.right + scrollX + 20;
        break;
      default:
        // Fallback au centre si position inconnue
        top = window.innerHeight / 2 - tooltipRect.height / 2 + scrollY;
        left = window.innerWidth / 2 - tooltipRect.width / 2 + scrollX;
        break;
    }

    // S'assurer que le tooltip reste dans les limites de l'écran
    const padding = 20;
    top = Math.max(padding + scrollY, Math.min(top, window.innerHeight + scrollY - tooltipRect.height - padding));
    left = Math.max(padding + scrollX, Math.min(left, window.innerWidth + scrollX - tooltipRect.width - padding));

    return { top, left };
  };

  // Effet pour trouver l'élément cible et calculer la position
  useEffect(() => {
    if (isActive && currentStepData) {
      const target = findTargetElement(currentStepData.target);
      setTargetElement(target);

      // Attendre que le tooltip soit rendu pour calculer sa position
      setTimeout(() => {
        const position = calculateTooltipPosition(target, currentStepData.position);
        setTooltipPosition(position);

        // Faire défiler vers l'élément cible si nécessaire
        // Éviter le scroll automatique pour certaines étapes
        const noScrollSteps = ['categories-panel', 'search-filters'];
        
        if (target && !noScrollSteps.includes(currentStepData?.id)) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [isActive, currentStepData]);

  // Détection du mode sombre
  useEffect(() => {
    const detectDarkMode = () => {
      // Méthode 1: CSS media query
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setIsDarkMode(true);
        return;
      }
      
      // Méthode 2: Vérifier les classes CSS du body ou html
      const htmlElement = document.documentElement;
      const bodyElement = document.body;
      
      if (htmlElement.classList.contains('dark') || 
          htmlElement.classList.contains('dark-mode') ||
          bodyElement.classList.contains('dark') || 
          bodyElement.classList.contains('dark-mode')) {
        setIsDarkMode(true);
        return;
      }
      
      // Méthode 3: Vérifier les styles computed du background
      const bgColor = window.getComputedStyle(document.body).backgroundColor;
      if (bgColor) {
        const rgb = bgColor.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          const [r, g, b] = rgb.map(Number);
          // Si la couleur de fond est sombre (luminosité < 128)
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          if (brightness < 128) {
            setIsDarkMode(true);
            return;
          }
        }
      }
      
      setIsDarkMode(false);
    };

    // Détecter immédiatement
    detectDarkMode();

    // Écouter les changements de préférence système
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsDarkMode(e.matches);
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      // Fallback pour les anciens navigateurs
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Effet pour démarrer automatiquement le tutoriel pour les nouveaux utilisateurs
  useEffect(() => {
    if (shouldShowTutorial()) {
      // Attendre un peu après le chargement de la page
      const timer = setTimeout(() => {
        startTutorial();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTutorial, startTutorial]);

  // Gestionnaire de redimensionnement de fenêtre
  useEffect(() => {
    const handleResize = () => {
      if (isActive && currentStepData && targetElement) {
        const position = calculateTooltipPosition(targetElement, currentStepData.position);
        setTooltipPosition(position);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, currentStepData, targetElement]);

  if (!isActive) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div className={`tutorial-overlay ${isDarkMode ? 'tutorial-dark-mode' : ''}`}>
        {/* Highlight pour l'élément cible */}
        {targetElement && (
          <div
            className="tutorial-highlight"
            style={{
              position: 'absolute',
              top: targetElement.getBoundingClientRect().top + window.scrollY - 5,
              left: targetElement.getBoundingClientRect().left + window.scrollX - 5,
              width: targetElement.offsetWidth + 10,
              height: targetElement.offsetHeight + 10,
              zIndex: 1005
            }}
          />
        )}

        {/* Tooltip */}
        <div
          ref={tooltipRef}
          className={`tutorial-tooltip ${currentStepData?.position || 'center'}`}
          style={{
            position: 'absolute',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: 1010
          }}
        >
          {/* Header du tooltip */}
          <div className="tutorial-tooltip-header">
            <div className="tutorial-step-info">
              <span className="tutorial-step-counter">
                {currentStep + 1} / {totalSteps}
              </span>
              <h3 className="tutorial-step-title">{currentStepData?.title}</h3>
            </div>
            <IconButton 
              size="small" 
              onClick={stopTutorial}
              className="tutorial-close-btn"
            >
              <CloseIcon />
            </IconButton>
          </div>

          {/* Barre de progression */}
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            className="tutorial-progress"
          />

          {/* Contenu du tooltip */}
          <div className="tutorial-tooltip-content">
            <p>{currentStepData?.description}</p>

            {/* Highlight spécial pour les étapes importantes */}
            {currentStepData?.highlight && (
              <div className="tutorial-important-notice">
                <PlayArrowIcon fontSize="small" />
                <span>Cette étape est essentielle pour utiliser la plateforme</span>
              </div>
            )}
          </div>

          {/* Boutons de navigation */}
          <div className="tutorial-tooltip-actions">
            <Button
              onClick={prevStep}
              disabled={isFirstStep}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              size="small"
            >
              Précédent
            </Button>

            <Button
              onClick={stopTutorial}
              variant="text"
              size="small"
              className="tutorial-skip-btn"
            >
              Passer le tutoriel
            </Button>

            <Button
              onClick={nextStep}
              endIcon={<ArrowForwardIcon />}
              variant="contained"
              size="small"
            >
              {isLastStep ? 'Terminer' : 'Suivant'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tutorial;